import { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { useLibraryStore } from "../store/libraryStore";
import { useTripStore } from "../store/tripStore";
import { useUIStore } from "../store/uiStore";
import { accommodationPalettes } from "../theme/colors";
import {
  filterAccommodationPois,
  isAccommodationPoi,
} from "../utils/accommodationSearch";
import { Modal } from "./Modal";
import { Button } from "./Button";
import { FormInput } from "./FormFields";

function formatNight(date: string): string {
  return new Date(`${date}T00:00:00`).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

/** Searchable, viewport-safe accommodation selection shared by calendar views. */
export function AccommodationPickerModal() {
  const date = useUIStore((s) => s.accommodationPickerDate);
  const closePicker = useUIStore((s) => s.closeAccommodationPicker);
  const openPOIEditor = useUIStore((s) => s.openPOIEditor);
  const openEditor = useUIStore((s) => s.openEditor);
  const pois = useLibraryStore((s) => s.pois);
  const tags = useLibraryStore((s) => s.tags);
  const sleep = useTripStore((s) => s.schedule.sleep);
  const configs = useTripStore((s) => s.schedule.accommodationConfigs);
  const setSleep = useTripStore((s) => s.setSleep);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (date) setQuery("");
  }, [date]);

  const filtered = useMemo(
    () =>
      filterAccommodationPois(
        pois.filter((poi) => isAccommodationPoi(poi, tags)),
        query,
      ),
    [pois, query, tags],
  );
  const selectedPoiId = date ? sleep[date] : undefined;

  const select = (poiId: string | null) => {
    if (!date) return;
    setSleep(date, poiId);
    closePicker();
  };

  const createNew = () => {
    closePicker();
    openPOIEditor("create");
  };

  const configure = (poiId: string) => {
    closePicker();
    openEditor({
      type: "accommodationConfig",
      mode: "edit",
      entityId: poiId,
    });
  };

  return (
    <Modal
      open={!!date}
      onClose={closePicker}
      title={date ? `Accommodation for ${formatNight(date)}` : "Accommodation"}
      description="Choose where you will sleep that night."
      icon="hotel"
      size="lg"
      footer={
        <Button onClick={createNew} variant="secondary" icon="add_circle">
          Create new accommodation
        </Button>
      }
    >
      <div className="flex flex-col gap-3">
        <div className="relative">
          <span className="material-symbols-outlined pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-lg text-tertiary">
            search
          </span>
          <FormInput
            value={query}
            onChange={setQuery}
            placeholder="Search accommodations"
            autoFocus
            className="pl-10"
            data-testid="accommodation-search"
          />
        </div>

        <div className="custom-scrollbar max-h-[min(55vh,28rem)] overflow-y-auto rounded-xl border border-border bg-subtle p-1.5">
          <button
            type="button"
            onClick={() => select(null)}
            aria-pressed={!selectedPoiId}
            className={clsx(
              "flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm transition-colors hover:bg-white",
              !selectedPoiId
                ? "bg-white font-bold text-primary shadow-sm"
                : "text-secondary",
            )}
          >
            <span className="material-symbols-outlined text-xl text-tertiary">
              block
            </span>
            <span className="flex-1">None</span>
            {!selectedPoiId && (
              <span className="material-symbols-outlined text-lg text-success">
                check
              </span>
            )}
          </button>

          {filtered.length === 0 ? (
            <div className="px-4 py-8 text-center">
              <span className="material-symbols-outlined text-3xl text-tertiary">
                search_off
              </span>
              <p className="mb-0 mt-2 text-sm font-semibold text-secondary">
                No accommodations match “{query.trim()}”.
              </p>
            </div>
          ) : (
            <ul className="m-0 flex list-none flex-col gap-1 p-0">
              {filtered.map((poi) => {
                const selected = selectedPoiId === poi.id;
                const palette =
                  accommodationPalettes[configs[poi.id]?.color ?? 0];
                return (
                  <li
                    key={poi.id}
                    className={clsx(
                      "flex items-stretch overflow-hidden rounded-lg transition-colors",
                      selected ? "bg-white shadow-sm" : "hover:bg-white",
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => select(poi.id)}
                      title={poi.name}
                      aria-pressed={selected}
                      className="flex min-w-0 flex-1 items-center gap-3 px-3 py-2.5 text-left"
                    >
                      <span
                        className="flex size-8 shrink-0 items-center justify-center rounded-lg border"
                        style={{
                          background: palette?.header,
                          borderColor: palette?.border,
                          color: palette?.text,
                        }}
                      >
                        <span className="material-symbols-outlined text-lg">
                          hotel
                        </span>
                      </span>
                      <span className="min-w-0 flex-1 break-words text-sm font-semibold text-primary">
                        {poi.name}
                      </span>
                      {selected && (
                        <span className="material-symbols-outlined shrink-0 text-lg text-success">
                          check
                        </span>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => configure(poi.id)}
                      aria-label={`Configure ${poi.name}`}
                      title="Configure color and times"
                      className="flex w-11 shrink-0 items-center justify-center border-l border-border text-tertiary transition-colors hover:bg-subtle hover:text-primary"
                    >
                      <span className="material-symbols-outlined text-lg">
                        tune
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </Modal>
  );
}
